/**
 * Music Theory Engine for Scale Degree Calculations
 * Uses only 12 primary major keys (no enharmonics)
 * Enhanced parsing for flats and speech recognition errors
 * FIXED: Sharp note parsing issue
 */

export class MusicTheory {
  // 12 primary major keys that musicians actually use (mix of sharps and flats)
  // Using the most common notation for each key
  static readonly KEYS: readonly string[] = ['C', 'Db', 'D', 'Eb', 'E', 'F', 'F#', 'G', 'Ab', 'A', 'Bb', 'B'];

  // All chromatic notes for calculations (sharps only for internal calculations)
  static readonly CHROMATIC_NOTES: readonly string[] = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];

  // Map between the practical keys and chromatic calculation notes
  static readonly KEY_TO_CHROMATIC: Record<string, string> = {
    'C': 'C',
    'Db': 'C#',  // D-flat major (5 flats)
    'D': 'D',
    'Eb': 'D#',  // E-flat major (3 flats)
    'E': 'E',
    'F': 'F',
    'F#': 'F#',  // F-sharp major (6 sharps) - some prefer Gb but F# is more common
    'G': 'G',
    'Ab': 'G#',  // A-flat major (4 flats)
    'A': 'A',
    'Bb': 'A#',  // B-flat major (2 flats)
    'B': 'B'
  };

  // Reverse mapping for converting chromatic notes back to practical keys
  static readonly CHROMATIC_TO_KEY: Record<string, string> = {
    'C': 'C',
    'C#': 'Db',
    'D': 'D',
    'D#': 'Eb',
    'E': 'E',
    'F': 'F',
    'F#': 'F#',
    'G': 'G',
    'G#': 'Ab',
    'A': 'A',
    'A#': 'Bb',
    'B': 'B'
  };

  // Enharmonic equivalents (both directions)
  static readonly ENHARMONIC_MAP: Record<string, string> = {
    'Db': 'C#',
    'C#': 'Db',
    'Eb': 'D#',
    'D#': 'Eb',
    'Gb': 'F#',
    'F#': 'Gb',
    'Ab': 'G#',
    'G#': 'Ab',
    'Bb': 'A#',
    'A#': 'Bb'
  };

  // Major scale intervals (semitones from root)
  static readonly MAJOR_SCALE_INTERVALS: readonly number[] = [0, 2, 4, 5, 7, 9, 11];

  /**
   * Get the note for a specific scale degree in a given key
   * @param key - The key (e.g., 'C', 'Eb', 'F#')
   * @param degree - Scale degree (1-7)
   * @returns The note name in practical notation
   */
  static getScaleDegree(key: string, degree: number): string {
    if (!this.KEYS.includes(key)) {
      throw new Error(`Invalid key: ${key}`);
    }
    
    if (degree < 1 || degree > 7) {
      throw new Error(`Invalid scale degree: ${degree}. Must be 1-7`);
    }

    // Convert key to chromatic note for calculation
    const chromaticKey = this.KEY_TO_CHROMATIC[key];
    const keyIndex = this.CHROMATIC_NOTES.indexOf(chromaticKey);
    const intervalSemitones = this.MAJOR_SCALE_INTERVALS[degree - 1];
    const targetNoteIndex = (keyIndex + intervalSemitones) % 12;
    const chromaticResult = this.CHROMATIC_NOTES[targetNoteIndex];
    
    // Convert back to practical notation, considering the key signature
    return this.getChromaticInKey(chromaticResult, key);
  }

  /**
   * Get the practical note name for a chromatic note within a specific key context
   * @param chromaticNote - Chromatic note (sharp-based)
   * @param key - The key context
   * @returns Practical note name
   */
  static getChromaticInKey(chromaticNote: string, key: string): string {
    // For flat keys, prefer flat notation for accidentals
    const flatKeys = ['Db', 'Eb', 'Ab', 'Bb'];
    
    if (flatKeys.includes(key)) {
      // In flat keys, convert sharps to flats when possible
      const sharpToFlat: Record<string, string> = {
        'C#': 'Db',
        'D#': 'Eb',
        'F#': 'Gb',
        'G#': 'Ab',
        'A#': 'Bb'
      };
      return sharpToFlat[chromaticNote] || chromaticNote;
    } else {
      // In sharp/natural keys, keep sharps or convert flats to sharps
      return chromaticNote;
    }
  }

  /**
   * Parse a note name from transcribed text
   * @param text - Transcribed text
   * @param isMobile - Whether this is for mobile device (more aggressive preprocessing)
   * @returns Parsed note name or null if not found
   */
  static parseNoteName(text: string | null, isMobile: boolean = false): string | null {
    if (!text) return null;

    const cleanText = text.toLowerCase().trim();
    console.log(`Parsing note from: "${text}" -> cleaned: "${cleanText}"`);

    // First, handle common speech recognition errors
    let processedText = this.preprocessSpeechErrors(cleanText, isMobile);
    console.log(`After preprocessing: "${processedText}"`);

    // Try to find note patterns
    const note = this.extractNote(processedText);

    if (note) {
      console.log(`Extracted note: "${note}"`);
      return note;
    }

    console.log('No note found in transcription');
    return null;
  }

  /**
   * Preprocess common speech recognition errors
   * @param text - Input text
   * @param isMobile - Whether this is for mobile device (more aggressive preprocessing)
   * @returns Processed text
   */
  static preprocessSpeechErrors(text: string, isMobile: boolean = false): string {
    let processed = text;

    // Replace hyphens with spaces to handle hyphenated notes like "E-flat"
    processed = processed.replace(/-/g, ' ');

    // First, handle repeated note names (like "see see" -> "c")
    const repeatedPatterns = [
      // Repeated note names
      /\b(hey|aye|eh)\s+\1\b/gi,      // "hey hey" or "aye aye" -> single "a"
      /\b(bee|be)\s+\1\b/gi,          // "bee bee" or "be be" -> single "b"
      /\b(sea|see)\s+\1\b/gi,         // "sea sea" or "see see" -> single "c"
      /\bdee\s+dee\b/gi,              // "dee dee" -> single "d"
      /\bee\s+ee\b/gi,                // "ee ee" -> single "e"
      /\beff\s+eff\b/gi,              // "eff eff" -> single "f"
      /\b(gee|jay)\s+\1\b/gi,         // "gee gee" or "jay jay" -> single "g"

      // Repeated with different variations
      /\b(sea|see)\s+(sea|see)\b/gi,  // "sea see" or "see sea" -> "c"
      /\b(hey|aye|eh)\s+(hey|aye|eh)\b/gi, // Any combination of A sounds
      /\b(bee|be)\s+(bee|be)\b/gi,    // Any combination of B sounds
      /\b(gee|jay)\s+(gee|jay)\b/gi,  // Any combination of G sounds

      // Enhanced patterns for problematic notes
      /\b(dee|the)\s+(dee|the)\b/gi,  // "dee dee", "the the", "dee the", "the dee" -> "d"
      /\bee\s+ee\b/gi,                // "ee ee" -> "e"
      /\b(bee|be)\s+(bee|be)\b/gi,    // "bee bee", "be be", "bee be", "be bee" -> "b"
    ];

    // Apply mobile-specific patterns if on mobile (simplified approach)
    if (isMobile) {
      // Simple mobile-specific corrections using regex replacement
      const mobileCorrections = [
        { pattern: /\btea\b/gi, replacement: 'd' },
        { pattern: /\bdear\b/gi, replacement: 'd' },
        { pattern: /\bdeep\b/gi, replacement: 'd' },
        { pattern: /\btheme\b/gi, replacement: 'd' },
        { pattern: /\bteeth\b/gi, replacement: 'd' },
        { pattern: /\bteh\b/gi, replacement: 'd' },
        { pattern: /\bduh\b/gi, replacement: 'd' },
        { pattern: /\beasy\b/gi, replacement: 'e' },
        { pattern: /\beat\b/gi, replacement: 'e' },
        { pattern: /\bear\b/gi, replacement: 'e' },
        { pattern: /\bee\b/gi, replacement: 'e' },
        { pattern: /\beea\b/gi, replacement: 'e' },
        { pattern: /\beh\b/gi, replacement: 'e' },
        { pattern: /\bbeef\b/gi, replacement: 'b' },
        { pattern: /\bbeach\b/gi, replacement: 'b' },
        { pattern: /\bbeat\b/gi, replacement: 'b' },
        { pattern: /\bbean\b/gi, replacement: 'b' },
        { pattern: /\bbear\b/gi, replacement: 'b' },
        { pattern: /\bbee\b/gi, replacement: 'b' },
        { pattern: /\bbe\b/gi, replacement: 'b' },
        { pattern: /\bbeh\b/gi, replacement: 'b' },
        { pattern: /\bbea\b/gi, replacement: 'b' }
      ];

      // Apply mobile corrections in order
      for (const { pattern, replacement } of mobileCorrections) {
        if (pattern.test(processed)) {
          processed = processed.replace(pattern, replacement);
        }
      }
    }

    // Apply repeated pattern corrections
    processed = processed.replace(repeatedPatterns[0], 'a');
    processed = processed.replace(repeatedPatterns[1], 'b');
    processed = processed.replace(repeatedPatterns[2], 'c');
    processed = processed.replace(repeatedPatterns[3], 'd');
    processed = processed.replace(repeatedPatterns[4], 'e');
    processed = processed.replace(repeatedPatterns[5], 'f');
    processed = processed.replace(repeatedPatterns[6], 'g');
    processed = processed.replace(repeatedPatterns[7], 'c');
    processed = processed.replace(repeatedPatterns[8], 'a');
    processed = processed.replace(repeatedPatterns[9], 'b');
    processed = processed.replace(repeatedPatterns[10], 'g');
    processed = processed.replace(repeatedPatterns[11], 'd'); // Enhanced: "dee dee", "the the", etc. -> "d"
    processed = processed.replace(repeatedPatterns[12], 'e'); // Enhanced: "ee ee" -> "e"
    processed = processed.replace(repeatedPatterns[13], 'b'); // Enhanced: "bee bee", "be be", etc. -> "b"

    // Common speech recognition substitutions
    const corrections = {
      // Note name corrections
      'hey': 'a',
      'hey hey': 'a',  // Fallback for any missed repeated patterns
      'aye': 'a',
      'eh': 'a',
      'bee': 'b',
      'be': 'b',
      'bee bee': 'b',  // Enhanced fallback for repeated B sounds
      'be be': 'b',
      'sea': 'c',
      'see': 'c',
      'see see': 'c',  // Fallback for any missed repeated patterns
      'sea sea': 'c',
      'dee': 'd',
      'dee dee': 'd',
      'the': 'd',      // Enhanced: "the" often recognized as "D"
      'the the': 'd',  // Enhanced: repeated "the" -> "D"
      'dee the': 'd',  // Enhanced: "dee the" -> "D"
      'the dee': 'd',  // Enhanced: "the dee" -> "D"
      'ee': 'e',       // Enhanced: "ee" -> "E"
      'ee ee': 'e',    // Enhanced: repeated "ee" -> "E"
      'eff': 'f',
      'gee': 'g',
      'jay': 'g', // Sometimes G sounds like J

      // Flat corrections - these are critical
      'b flat': 'b flat',
      'be flat': 'b flat',
      'bee flat': 'b flat',
      'a flat': 'a flat',
      'hey flat': 'a flat',
      'aye flat': 'a flat',
      'd flat': 'd flat',
      'dee flat': 'd flat',
      'e flat': 'e flat',
      'ee flat': 'e flat',
      'g flat': 'g flat',
      'gee flat': 'g flat',

      // Sharp variations
      'c sharp': 'c sharp',
      'sea sharp': 'c sharp',
      'see sharp': 'c sharp',
      't-shirt': 'd sharp',
      't shirt': 'd sharp',
      'dee shirt': 'd sharp',
      'd shirt': 'd sharp',
      'd sharp': 'd sharp',
      'dee sharp': 'd sharp',
      'f sharp': 'f sharp',
      'eff sharp': 'f sharp',
      'g sharp': 'g sharp',
      'gee sharp': 'g sharp',
      'a sharp': 'a sharp',
      'hey sharp': 'a sharp',
      'aye sharp': 'a sharp',

      // Remove filler words
      'um': '',
      'uh': '',
      'note': '',
      'is': '',
      'it': '',

      // Mobile-specific corrections (only applied on mobile)
      ...(isMobile && {
        'tea': 'd',       // Mobile "tea" -> "D"
        'dear': 'd',      // Mobile "dear" -> "D"
        'deep': 'd',      // Mobile "deep" -> "D"
        'theme': 'd',     // Mobile "theme" -> "D"
        'teeth': 'd',     // Mobile "teeth" -> "D"
        'easy': 'e',      // Mobile "easy" -> "E"
        'eat': 'e',       // Mobile "eat" -> "E"
        'ear': 'e',       // Mobile "ear" -> "E"
        'bean': 'b',      // Mobile "bean" -> "B"
        'beat': 'b',      // Mobile "beat" -> "B"
        'beef': 'b',      // Mobile "beef" -> "B"
        'bear': 'b',      // Mobile "bear" -> "B"
        'beach': 'b',     // Mobile "beach" -> "B"
        'teh': 'd',       // Mobile "teh" -> "D"
        'duh': 'd',       // Mobile "duh" -> "D"
        'beh': 'b',       // Mobile "beh" -> "B"
        'eea': 'e'        // Mobile "eea" -> "E"
      })
    };

    // Apply corrections
    for (const [error, correction] of Object.entries(corrections)) {
      const regex = new RegExp(`\\b${error}\\b`, 'gi');
      processed = processed.replace(regex, correction);
    }

    // Clean up extra spaces
    processed = processed.replace(/\s+/g, ' ').trim();

    return processed;
  }

  /**
   * Extract note from processed text
   * @param text - Processed text
   * @returns Note name or null
   */
  static extractNote(text: string): string | null {
    // Pattern matching for notes with flats and sharps
    // FIXED: More specific patterns and better ordering
    const patterns = [
      // Flat notes with explicit "flat" word (highest priority)
      /\b([abcdefg])\s+flat\b/i,
      /\b([abcdefg])\s*flat\b/i,
      
      // Sharp notes with explicit "sharp" word (high priority)
      /\b([abcdefg])\s+sharp\b/i,
      /\b([abcdefg])\s*sharp\b/i,
      
      // Flat notes with 'b' suffix
      /\b([abcdefg])b/i,
      /\b([abcdefg])\s*♭/i,
    
      // Sharp notes with '#' suffix
      /\b([abcdefg])\s*#/i,
      /\b([abcdefg])\s*♯/i,
      
      // Natural notes (lowest priority to avoid false matches)
      /\b([abcdefg])(?!\s*(sharp|flat|#|♯|♭|b))\b/i
    ];

    for (let i = 0; i < patterns.length; i++) {
      const pattern = patterns[i];
      const match = text.match(pattern);
      
      if (match) {
        let note = match[1].toUpperCase();
        console.log(`Pattern ${i} matched: "${match[0]}" -> note: "${note}"`);
        
        // Determine if it's flat, sharp, or natural based on pattern index
        if (i <= 1) {
          // Flat with "flat" word
          const flatNote = note + 'b';
          console.log(`Processing as flat: ${flatNote}`);
          
          // Check if this flat note is in our key system
          if (this.KEYS.includes(flatNote)) {
            return flatNote;
          }
          
          // Convert to enharmonic equivalent if needed
          if (this.ENHARMONIC_MAP[flatNote]) {
            const equivalent = this.ENHARMONIC_MAP[flatNote];
            console.log(`Converting ${flatNote} to enharmonic equivalent: ${equivalent}`);
            return equivalent;
          }
          
          // Handle invalid flats (like Cb = B, Fb = E)
          const flatToNatural: Record<string, string> = {
            'Cb': 'B',    // C flat = B
            'Fb': 'E'     // F flat = E
          };
          
          if (flatToNatural[flatNote]) {
            console.log(`Converting invalid flat ${flatNote} to natural: ${flatToNatural[flatNote]}`);
            return flatToNatural[flatNote];
          }
          
        } else if (i >= 2 && i <= 3) {
          // Sharp with "sharp" word
          const sharpNote = note + '#';
          console.log(`Processing as sharp: ${sharpNote}`);

          // Check if this sharp note is in our key system first
          if (this.KEYS.includes(sharpNote)) {
            console.log(`Sharp note ${sharpNote} found in KEYS, returning it directly`);
            return sharpNote;
          }

          // Check if this sharp note makes sense as a sharp
          const validSharps = ['C#', 'D#', 'F#', 'G#', 'A#'];
          if (validSharps.includes(sharpNote)) {
            // Return the sharp as requested - don't convert valid sharps to flats
            console.log(`Valid sharp ${sharpNote}, returning as requested`);
            return sharpNote;
          }
          
          // Handle invalid sharps (like E# = F, B# = C)
          const sharpToNatural: Record<string, string> = {
            'E#': 'F',    // E sharp = F
            'B#': 'C'     // B sharp = C
          };
          
          if (sharpToNatural[sharpNote]) {
            console.log(`Converting invalid sharp ${sharpNote} to natural: ${sharpToNatural[sharpNote]}`);
            return sharpToNatural[sharpNote];
          }
          
        } else if (i >= 4 && i <= 5) {
          // Flat with 'b' or ♭ symbol
          const flatNote = note + 'b';
          console.log(`Processing as flat (symbol): ${flatNote}`);
          
          if (this.KEYS.includes(flatNote)) {
            return flatNote;
          }
          
          // Convert to enharmonic equivalent if needed
          if (this.ENHARMONIC_MAP[flatNote]) {
            const equivalent = this.ENHARMONIC_MAP[flatNote];
            console.log(`Converting ${flatNote} to enharmonic equivalent: ${equivalent}`);
            return equivalent;
          }
          
          const flatToNatural: Record<string, string> = {
            'Cb': 'B',
            'Fb': 'E'
          };
          
          if (flatToNatural[flatNote]) {
            console.log(`Converting invalid flat ${flatNote} to natural: ${flatToNatural[flatNote]}`);
            return flatToNatural[flatNote];
          }
          
        } else if (i >= 6 && i <= 7) {
          // Sharp with '#' or ♯ symbol
          const sharpNote = note + '#';
          console.log(`Processing as sharp (symbol): ${sharpNote}`);

          // Check if this sharp note is in our key system first
          if (this.KEYS.includes(sharpNote)) {
            console.log(`Sharp note ${sharpNote} found in KEYS, returning it directly`);
            return sharpNote;
          }

          const validSharps = ['C#', 'D#', 'F#', 'G#', 'A#'];
          if (validSharps.includes(sharpNote)) {
            // Return the sharp as requested - don't convert valid sharps to flats
            console.log(`Valid sharp ${sharpNote}, returning as requested`);
            return sharpNote;
          }
          
          const sharpToNatural: Record<string, string> = {
            'E#': 'F',
            'B#': 'C'
          };
          
          if (sharpToNatural[sharpNote]) {
            console.log(`Converting invalid sharp ${sharpNote} to natural: ${sharpToNatural[sharpNote]}`);
            return sharpToNatural[sharpNote];
          }
          
        } else {
          // Natural note
          console.log(`Processing as natural: ${note}`);
          if (this.KEYS.includes(note)) {
            return note;
          }
        }
      }
    }

    // Fallback: try exact word matching for difficult cases
    const wordMap: Record<string, string> = {
      'a': 'A',
      'b': 'B',
      'c': 'C',
      'd': 'D',
      'e': 'E',
      'f': 'F',
      'g': 'G',
      // Enhanced phonetic variations
      'dee': 'D',
      'the': 'D',
      'ee': 'E',
      'bee': 'B',
      'be': 'B'
    };

    const words = text.split(/\s+/);
    for (const word of words) {
      if (wordMap[word]) {
        console.log(`Fallback word match: "${word}" -> ${wordMap[word]}`);
        return wordMap[word];
      }
    }

    return null;
  }

  /**
   * Generate a random key from the primary keys
   * @returns Random key
   */
  static getRandomKey(): string {
    return this.KEYS[Math.floor(Math.random() * this.KEYS.length)];
  }

  /**
   * Generate a random scale degree (2-7)
   * @returns Random scale degree
   */
  static getRandomDegree(): number {
    return Math.floor(Math.random() * 6) + 2; // 2-7
  }

  /**
   * Validate if a note name is correct for the given key and degree
   * @param key - The key
   * @param degree - Scale degree
   * @param answer - User's answer
   * @returns True if correct
   */
  static isCorrectAnswer(key: string, degree: number, answer: string | null): boolean {
    if (!answer) return false;
    
    const correctNote = this.getScaleDegree(key, degree);
    console.log(`Checking answer: "${answer}" vs correct: "${correctNote}"`);
    
    // Direct match
    if (correctNote === answer) {
      console.log('Direct match found');
      return true;
    }
    
    // Check enharmonic equivalents in both directions
    if (this.ENHARMONIC_MAP[correctNote] === answer) {
      console.log('Enharmonic match found (correct->answer)');
      return true;
    }
    if (this.ENHARMONIC_MAP[answer] === correctNote) {
      console.log('Enharmonic match found (answer->correct)');
      return true;
    }
    
    // Also check if both notes represent the same chromatic pitch
    // Convert both to chromatic notation for comparison
    const correctChromatic = this.KEY_TO_CHROMATIC[correctNote] || correctNote;
    const answerChromatic = this.convertToChromatic(answer);
    
    if (correctChromatic === answerChromatic) {
      console.log(`Chromatic match found: ${correctNote} (${correctChromatic}) = ${answer} (${answerChromatic})`);
      return true;
    }
    
    console.log('No match found');
    return false;
  }

  /**
   * Convert a note to its chromatic (sharp-based) equivalent
   * @param note - Note name
   * @returns Chromatic note name
   */
  static convertToChromatic(note: string): string {
    // If it's already a chromatic note, return it
    if (this.CHROMATIC_NOTES.includes(note)) {
      return note;
    }
    
    // Check if it's a flat note that needs conversion
    const flatToSharp: Record<string, string> = {
      'Db': 'C#',
      'Eb': 'D#',
      'Gb': 'F#',
      'Ab': 'G#',
      'Bb': 'A#'
    };
    
    return flatToSharp[note] || note;
  }

  /**
   * Get all scale degrees for a key (for testing/validation)
   * @param key - The key
   * @returns Object with degrees 1-7 and their notes
   */
  static getFullScale(key: string): Record<number, string> {
    const scale: Record<number, string> = {};
    for (let degree = 1; degree <= 7; degree++) {
      scale[degree] = this.getScaleDegree(key, degree);
    }
    return scale;
  }

  /**
   * Get the speakable version of a key name for TTS
   * @param key - The key name (e.g., 'Ab', 'F#')
   * @returns Speakable key name (e.g., 'A flat', 'F sharp')
   */
  static getSpeakableKey(key: string): string {
    if (!this.KEYS.includes(key)) {
      throw new Error(`Invalid key: ${key}`);
    }

    // Replace 'b' with ' flat' and '#' with ' sharp'
    return key.replace(/b/g, ' flat').replace(/#/g, ' sharp');
  }

  /**
   * Get the speakable version of a note name for TTS
   * @param note - The note name (e.g., 'Eb', 'F#')
   * @returns Speakable note name (e.g., 'E flat', 'F sharp')
   */
  static getSpeakableNote(note: string): string {
    // Replace 'b' with ' flat' and '#' with ' sharp'
    return note.replace(/b/g, ' flat').replace(/#/g, ' sharp');
  }

  /**
   * Determine if a key uses sharp notation for accidentals
   * @param key - The key to check
   * @returns True if the key uses sharps
   */
  static isSharpKey(key: string): boolean {
    return ['G', 'D', 'A', 'E', 'B', 'F#'].includes(key);
  }

  /**
   * Determine if a key uses flat notation for accidentals
   * @param key - The key to check
   * @returns True if the key uses flats
   */
  static isFlatKey(key: string): boolean {
    return ['F', 'Bb', 'Eb', 'Ab', 'Db'].includes(key);
  }

  /**
   * Get all 12 chromatic notes with appropriate accidental notation for a given key
   * @param key - The key context to determine sharp vs flat notation
   * @returns Array of 12 chromatic notes with appropriate accidentals
   */
  static getChromaticNotesForKey(key: string): string[] {
    if (this.isFlatKey(key)) {
      // For flat keys, use flat notation for accidentals
      return ['C', 'Db', 'D', 'Eb', 'E', 'F', 'Gb', 'G', 'Ab', 'A', 'Bb', 'B'];
    } else {
      // For sharp keys and C major, use sharp notation for accidentals
      return ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
    }
  }

  /**
   * Determine if a note is an accidental (contains sharp or flat)
   * @param note - The note to check
   * @returns True if the note is an accidental
   */
  static isAccidental(note: string): boolean {
    return note.includes('#') || note.includes('b');
  }

  /**
   * Get a description of the key signature type for display
   * @param key - The key to describe
   * @returns Description string
   */
  static getKeySignatureDescription(key: string): string {
    if (this.isFlatKey(key)) {
      return 'Flat key - accidentals shown as flats';
    } else if (this.isSharpKey(key)) {
      return 'Sharp key - accidentals shown as sharps';
    } else {
      return 'Natural key - accidentals shown as sharps';
    }
  }

  /**
   * Find all keys where the given note appears at the specified scale degree
   * @param note - The note to find (e.g., 'F', 'Bb', 'C#')
   * @param degree - Scale degree (2-7)
   * @returns Array of keys where the note appears at that degree
   */
  static findKeyForScaleDegree(note: string, degree: number): string[] {
    if (degree < 2 || degree > 7) {
      throw new Error(`Invalid scale degree: ${degree}. Must be 2-7`);
    }

    const foundKeys: string[] = [];
    
    // Check each key to see if the given note appears at the specified degree
    for (const key of this.KEYS) {
      try {
        const scaleDegreeNote = this.getScaleDegree(key, degree);
        
        // Direct match
        if (scaleDegreeNote === note) {
          foundKeys.push(key);
          continue;
        }
        
        // Check enharmonic equivalents
        if (this.ENHARMONIC_MAP[scaleDegreeNote] === note || 
            this.ENHARMONIC_MAP[note] === scaleDegreeNote) {
          foundKeys.push(key);
          continue;
        }
        
        // Check chromatic equivalence
        const scaleChromatic = this.convertToChromatic(scaleDegreeNote);
        const noteChromatic = this.convertToChromatic(note);
        if (scaleChromatic === noteChromatic) {
          foundKeys.push(key);
        }
      } catch (error) {
        // Skip invalid combinations
        continue;
      }
    }
    
    return foundKeys;
  }

  /**
   * Find the primary (most common) key where the given note appears at the specified scale degree
   * @param note - The note to find (e.g., 'F', 'Bb', 'C#')
   * @param degree - Scale degree (2-7)
   * @returns The most common key, or the first found key if multiple exist
   */
  static findPrimaryKeyForScaleDegree(note: string, degree: number): string {
    const keys = this.findKeyForScaleDegree(note, degree);
    
    if (keys.length === 0) {
      throw new Error(`No key found where ${note} is the ${degree}th degree`);
    }
    
    // Return the first key found (they're ordered by commonality in KEYS array)
    // The KEYS array is already ordered with more common keys first
    return keys[0];
  }

  /**
   * Parse a key name from transcribed text (similar to parseNoteName but for keys)
   * @param text - Transcribed text
   * @param isMobile - Whether this is for mobile device (more aggressive preprocessing)
   * @returns Parsed key name or null if not found
   */
  static parseKeyName(text: string | null, isMobile: boolean = false): string | null {
    if (!text) return null;

    const cleanText = text.toLowerCase().trim();
    console.log(`Parsing key from: "${text}" -> cleaned: "${cleanText}"`);

    // First, handle common speech recognition errors for key names
    let processedText = this.preprocessKeyNameSpeechErrors(cleanText, isMobile);
    console.log(`After key preprocessing: "${processedText}"`);

    // Try to find key patterns
    const key = this.extractKeyName(processedText);

    if (key) {
      console.log(`Extracted key: "${key}"`);
      return key;
    }

    console.log('No key found in transcription');
    return null;
  }

  /**
   * Preprocess common speech recognition errors for key names
   * @param text - Input text
   * @param isMobile - Whether this is for mobile device (more aggressive preprocessing)
   * @returns Processed text
   */
  static preprocessKeyNameSpeechErrors(text: string, isMobile: boolean = false): string {
    let processed = text;

    // Replace hyphens with spaces to handle hyphenated keys like "C-sharp"
    processed = processed.replace(/-/g, ' ');

    // Remove "major" and "minor" since we only deal with major keys
    processed = processed.replace(/\bmajor\b/gi, '');
    processed = processed.replace(/\bminor\b/gi, '');

    // Common key name corrections
    const corrections = {
      // Note name corrections (reuse from note parsing)
      'hey': 'a',
      'aye': 'a',
      'eh': 'a',
      'bee': 'b',
      'be': 'b',
      'sea': 'c',
      'see': 'c',
      'dee': 'd',
      'the': 'd',
      'ee': 'e',
      'eff': 'f',
      'gee': 'g',
      'jay': 'g',

      // Key-specific corrections
      'c sharp major': 'c sharp',
      'sea sharp major': 'c sharp',
      'see sharp major': 'c sharp',
      'd flat major': 'd flat',
      'dee flat major': 'd flat',
      'd sharp major': 'd sharp',
      'dee sharp major': 'd sharp',
      'e flat major': 'e flat',
      'ee flat major': 'e flat',
      'f sharp major': 'f sharp',
      'eff sharp major': 'f sharp',
      'g flat major': 'g flat',
      'gee flat major': 'g flat',
      'g sharp major': 'g sharp',
      'gee sharp major': 'g sharp',
      'a flat major': 'a flat',
      'hey flat major': 'a flat',
      'aye flat major': 'a flat',
      'a sharp major': 'a sharp',
      'hey sharp major': 'a sharp',
      'aye sharp major': 'a sharp',
      'b flat major': 'b flat',
      'bee flat major': 'b flat',
      'be flat major': 'b flat',

      // Remove filler words
      'um': '',
      'uh': '',
      'key': '',
      'is': '',
      'it': '',
      'of': '',

      // Mobile-specific corrections
      ...(isMobile && {
        'tea': 'd',
        'dear': 'd',
        'deep': 'd',
        'theme': 'd',
        'teeth': 'd',
        'easy': 'e',
        'eat': 'e',
        'ear': 'e',
        'bean': 'b',
        'beat': 'b',
        'beef': 'b',
        'bear': 'b',
        'beach': 'b',
        'teh': 'd',
        'duh': 'd',
        'beh': 'b',
        'eea': 'e'
      })
    };

    // Apply corrections
    for (const [error, correction] of Object.entries(corrections)) {
      const regex = new RegExp(`\\b${error}\\b`, 'gi');
      processed = processed.replace(regex, correction);
    }

    // Clean up extra spaces
    processed = processed.replace(/\s+/g, ' ').trim();

    return processed;
  }

  /**
   * Extract key name from processed text
   * @param text - Processed text
   * @returns Key name or null
   */
  static extractKeyName(text: string): string | null {
    // Pattern matching for keys with flats and sharps
    const patterns = [
      // Flat keys with explicit "flat" word (highest priority)
      /\b([abcdefg])\s+flat\b/i,
      /\b([abcdefg])\s*flat\b/i,
      
      // Sharp keys with explicit "sharp" word (high priority)
      /\b([abcdefg])\s+sharp\b/i,
      /\b([abcdefg])\s*sharp\b/i,
      
      // Flat keys with 'b' suffix
      /\b([abcdefg])b/i,
      /\b([abcdefg])\s*♭/i,
    
      // Sharp keys with '#' suffix
      /\b([abcdefg])\s*#/i,
      /\b([abcdefg])\s*♯/i,
      
      // Natural keys (lowest priority to avoid false matches)
      /\b([abcdefg])(?!\s*(sharp|flat|#|♯|♭|b))\b/i
    ];

    for (let i = 0; i < patterns.length; i++) {
      const pattern = patterns[i];
      const match = text.match(pattern);
      
      if (match) {
        let key = match[1].toUpperCase();
        console.log(`Key pattern ${i} matched: "${match[0]}" -> key: "${key}"`);
        
        // Determine if it's flat, sharp, or natural based on pattern index
        if (i <= 1) {
          // Flat with "flat" word
          const flatKey = key + 'b';
          console.log(`Processing as flat key: ${flatKey}`);
          
          // Check if this flat key is in our key system
          if (this.KEYS.includes(flatKey)) {
            return flatKey;
          }
          
          // Convert to enharmonic equivalent if needed
          if (this.ENHARMONIC_MAP[flatKey]) {
            const equivalent = this.ENHARMONIC_MAP[flatKey];
            console.log(`Converting ${flatKey} to enharmonic equivalent: ${equivalent}`);
            if (this.KEYS.includes(equivalent)) {
              return equivalent;
            }
          }
          
        } else if (i >= 2 && i <= 3) {
          // Sharp with "sharp" word
          const sharpKey = key + '#';
          console.log(`Processing as sharp key: ${sharpKey}`);

          // Check if this sharp key is in our key system
          if (this.KEYS.includes(sharpKey)) {
            console.log(`Sharp key ${sharpKey} found in KEYS, returning it directly`);
            return sharpKey;
          }

          // Convert to enharmonic equivalent if needed
          if (this.ENHARMONIC_MAP[sharpKey]) {
            const equivalent = this.ENHARMONIC_MAP[sharpKey];
            console.log(`Converting ${sharpKey} to enharmonic equivalent: ${equivalent}`);
            if (this.KEYS.includes(equivalent)) {
              return equivalent;
            }
          }
          
        } else if (i >= 4 && i <= 5) {
          // Flat with 'b' or ♭ symbol
          const flatKey = key + 'b';
          console.log(`Processing as flat key (symbol): ${flatKey}`);
          
          if (this.KEYS.includes(flatKey)) {
            return flatKey;
          }
          
          // Convert to enharmonic equivalent if needed
          if (this.ENHARMONIC_MAP[flatKey]) {
            const equivalent = this.ENHARMONIC_MAP[flatKey];
            console.log(`Converting ${flatKey} to enharmonic equivalent: ${equivalent}`);
            if (this.KEYS.includes(equivalent)) {
              return equivalent;
            }
          }
          
        } else if (i >= 6 && i <= 7) {
          // Sharp with '#' or ♯ symbol
          const sharpKey = key + '#';
          console.log(`Processing as sharp key (symbol): ${sharpKey}`);

          // Check if this sharp key is in our key system
          if (this.KEYS.includes(sharpKey)) {
            console.log(`Sharp key ${sharpKey} found in KEYS, returning it directly`);
            return sharpKey;
          }

          // Convert to enharmonic equivalent if needed
          if (this.ENHARMONIC_MAP[sharpKey]) {
            const equivalent = this.ENHARMONIC_MAP[sharpKey];
            console.log(`Converting ${sharpKey} to enharmonic equivalent: ${equivalent}`);
            if (this.KEYS.includes(equivalent)) {
              return equivalent;
            }
          }
          
        } else {
          // Natural key
          console.log(`Processing as natural key: ${key}`);
          if (this.KEYS.includes(key)) {
            return key;
          }
        }
      }
    }

    // Fallback: try exact word matching for difficult cases
    const wordMap: Record<string, string> = {
      'a': 'A',
      'b': 'B',
      'c': 'C',
      'd': 'D',
      'e': 'E',
      'f': 'F',
      'g': 'G',
      // Enhanced phonetic variations
      'dee': 'D',
      'the': 'D',
      'ee': 'E',
      'bee': 'B',
      'be': 'B'
    };

    const words = text.split(/\s+/);
    for (const word of words) {
      if (wordMap[word] && this.KEYS.includes(wordMap[word])) {
        console.log(`Fallback key word match: "${word}" -> ${wordMap[word]}`);
        return wordMap[word];
      }
    }

    return null;
  }

  /**
   * Validate if a key name is correct for inverse challenges
   * @param expectedKey - The expected correct key
   * @param userAnswer - User's answer (key name)
   * @returns True if correct
   */
  static isValidKeyAnswer(expectedKey: string, userAnswer: string | null): boolean {
    if (!userAnswer) return false;
    
    console.log(`Checking key answer: "${userAnswer}" vs expected: "${expectedKey}"`);
    
    // Direct match
    if (expectedKey === userAnswer) {
      console.log('Direct key match found');
      return true;
    }
    
    // Check enharmonic equivalents in both directions
    if (this.ENHARMONIC_MAP[expectedKey] === userAnswer) {
      console.log('Enharmonic key match found (expected->answer)');
      return true;
    }
    if (this.ENHARMONIC_MAP[userAnswer] === expectedKey) {
      console.log('Enharmonic key match found (answer->expected)');
      return true;
    }
    
    // Also check if both keys represent the same chromatic pitch
    // Convert both to chromatic notation for comparison
    const expectedChromatic = this.KEY_TO_CHROMATIC[expectedKey] || expectedKey;
    const answerChromatic = this.convertToChromatic(userAnswer);
    
    if (expectedChromatic === answerChromatic) {
      console.log(`Chromatic key match found: ${expectedKey} (${expectedChromatic}) = ${userAnswer} (${answerChromatic})`);
      return true;
    }
    
    console.log('No key match found');
    return false;
  }

  /**
   * Get the speakable version of a key name for TTS (enhanced version of getSpeakableKey)
   * @param key - The key name (e.g., 'Ab', 'F#')
   * @returns Speakable key name (e.g., 'A flat major', 'F sharp major')
   */
  static getSpeakableKeyName(key: string): string {
    if (!this.KEYS.includes(key)) {
      throw new Error(`Invalid key: ${key}`);
    }

    // Replace 'b' with ' flat' and '#' with ' sharp', then add 'major'
    const speakableKey = key.replace(/b/g, ' flat').replace(/#/g, ' sharp');
    return `${speakableKey} major`;
  }

  /**
   * Test the note parsing function (for debugging)
   * @param text - Text to test
   * @returns Parsing results
   */
  static testParsing(text: string): { original: string; preprocessed: string; extracted: string | null } {
    const preprocessed = this.preprocessSpeechErrors(text.toLowerCase().trim());
    const extracted = this.extractNote(preprocessed);

    return {
      original: text,
      preprocessed: preprocessed,
      extracted: extracted
    };
  }

  /**
   * Test the key parsing function (for debugging)
   * @param text - Text to test
   * @returns Parsing results
   */
  static testKeyParsing(text: string): { original: string; preprocessed: string; extracted: string | null } {
    const preprocessed = this.preprocessKeyNameSpeechErrors(text.toLowerCase().trim());
    const extracted = this.extractKeyName(preprocessed);

    return {
      original: text,
      preprocessed: preprocessed,
      extracted: extracted
    };
  }
}

// Export for testing
export default MusicTheory;